import { useTranslation } from "react-i18next";
import { TWclassNames } from "../../utils/Div";

/** Progress for `finish_work_order` checklist rows from API `operations`. */
export function getWorkOrderFinishProgress(workOrder) {
  const finishOps =
    workOrder?.operations?.filter((op) => op.name === "finish_work_order") ||
    [];
  const satisfied = finishOps.filter(
    (op) => op.can_be_completed === true
  ).length;
  return {
    total: finishOps.length,
    satisfied,
    isAllSatisfied:
      finishOps.length > 0 && satisfied === finishOps.length,
    hasBlocking: finishOps.some((op) => op.can_be_completed === false),
  };
}

/**
 * Status card: whether the work order can be completed (ferdigstilles), with progress bar.
 * Mirrors the finish section pattern in report-view-v2.js.
 */
export function WorkOrderCompletionStatusCard({
  workOrder,
  canOpenFinish,
  onOpenFinish,
}) {
  const { t } = useTranslation();

  if (!workOrder?.id) return null;

  const isDone = Boolean(workOrder.is_completed);
  const canFinishByStatus = ["draft", "ongoing", "rejected"].includes(
    workOrder.status
  );
  const progress = getWorkOrderFinishProgress(workOrder);

  if (!canFinishByStatus && !isDone) {
    return (
      <div className="overflow-hidden rounded-lg border-2 border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-800">
            {t("work_order.completion.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {t("work_order.completion.status_not_applicable")}
          </p>
        </div>
      </div>
    );
  }

  const pct =
    progress.total > 0
      ? Math.min((progress.satisfied / progress.total) * 100, 100)
      : 0;

  const borderTone = isDone
    ? "border-green-200"
    : progress.isAllSatisfied
      ? "border-green-200"
      : progress.hasBlocking
        ? "border-amber-200"
        : "border-gray-200";

  const headerTone = isDone
    ? "border-green-200 bg-green-50"
    : progress.isAllSatisfied
      ? "border-green-200 bg-green-50"
      : progress.hasBlocking
        ? "border-amber-200 bg-amber-50"
        : "border-gray-200 bg-gray-50";

  const titleTone = isDone
    ? "text-green-900"
    : progress.isAllSatisfied
      ? "text-green-900"
      : progress.hasBlocking
        ? "text-amber-900"
        : "text-gray-900";

  const subtitleTone = isDone
    ? "text-green-700"
    : progress.isAllSatisfied
      ? "text-green-700"
      : progress.hasBlocking
        ? "text-amber-800"
        : "text-gray-600";

  return (
    <div
      className={TWclassNames(
        "overflow-hidden rounded-lg border-2 bg-white shadow-sm",
        borderTone
      )}
    >
      <div className={TWclassNames("border-b px-6 py-4", headerTone)}>
        <h3 className={TWclassNames("text-lg font-semibold", titleTone)}>
          {isDone
            ? t("work_order.completion.heading_done")
            : t("work_order.completion.heading_pending")}
        </h3>
        <p className={TWclassNames("mt-1 text-sm", subtitleTone)}>
          {isDone
            ? t("work_order.completion.sub_done")
            : progress.hasBlocking
              ? t("work_order.completion.sub_blocked")
              : t("work_order.completion.sub_ready")}
        </p>
      </div>
      <div className="p-6">
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {isDone
                ? t("work_order.completion.label_status")
                : t("work_order.completion.label_progress")}
            </span>
            <span className="text-sm text-gray-500">
              {isDone
                ? t("work_order.completion.value_done")
                : progress.total > 0
                  ? `${progress.satisfied} / ${progress.total}`
                  : t("work_order.completion.no_checks")}
            </span>
          </div>
          {isDone ? (
            <div className="h-2 w-full rounded-full bg-green-200">
              <div className="h-2 w-full rounded-full bg-green-600" />
            </div>
          ) : (
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className={TWclassNames(
                  "h-2 rounded-full transition-all duration-300",
                  progress.isAllSatisfied ? "bg-green-500" : "bg-teal-500"
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
        </div>
        {canOpenFinish && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onOpenFinish}
              className="rounded-md bg-teal-600 px-6 py-3 font-medium text-white transition-colors hover:bg-teal-700"
            >
              {isDone
                ? t("work_order.completion.cta_view")
                : t("work_order.completion.cta_finish")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
